import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { ArrowsClockwise, TrendUp, TrendDown, Sliders, FloppyDisk } from "@phosphor-icons/react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

function fmtInr(v) {
    if (v == null || Number.isNaN(v)) return "—";
    const sign = v < 0 ? "− " : "";
    return `${sign}₹ ${inr(Math.abs(v))}`;
}

export default function NetWorth() {
    const [snap, setSnap] = useState(null);
    const [history, setHistory] = useState(null);
    const [valuation, setValuation] = useState({ rm_pct: 50, wip_pct: 75, fg_pct: 75 });
    const [origValuation, setOrigValuation] = useState({ rm_pct: 50, wip_pct: 75, fg_pct: 75 });
    const [months, setMonths] = useState(12);
    const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [s, h, v] = await Promise.all([
                api.get("/reports/networth", { params: { as_of: asOf } }),
                api.get("/reports/networth/history", { params: { months } }),
                api.get("/settings/networth"),
            ]);
            setSnap(s.data);
            setHistory(h.data);
            const val = { rm_pct: v.data.rm_pct, wip_pct: v.data.wip_pct, fg_pct: v.data.fg_pct };
            setValuation(val);
            setOrigValuation(val);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [asOf, months]);
    useEffect(() => { refresh(); }, [refresh]);

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put("/settings/networth", valuation);
            toast.success("Valuation updated");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingSettings(false); }
    };

    const nw = snap?.net_worth ?? 0;
    const positive = nw >= 0;
    const dirty = origValuation.rm_pct !== valuation.rm_pct
        || origValuation.wip_pct !== valuation.wip_pct
        || origValuation.fg_pct !== valuation.fg_pct;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Net worth"
                sub="If you stopped operations today — collected receivables, paid down payables and loans, sold stock at discount, and cashed out fixed assets — this is what would be left."
                right={
                    <div className="flex items-center gap-2">
                        <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-40" data-testid="nw-asof" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="nw-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                {/* Hero */}
                <div className="rounded-md p-6 border" style={{
                    borderColor: "var(--se-border)",
                    background: positive
                        ? "linear-gradient(135deg,#ecfdf5 0%,#f7fee7 100%)"
                        : "linear-gradient(135deg,#fef2f2 0%,#fffbeb 100%)",
                }} data-testid="nw-hero">
                    <div className="text-xs tracking-widest uppercase" style={{ color: positive ? "#065f46" : "#991b1b" }}>
                        Net worth · as of {snap?.as_of}
                    </div>
                    <div className="se-num text-5xl mt-2" style={{ color: positive ? "#065f46" : "#991b1b" }}>
                        {positive ? <TrendUp size={28} className="inline mr-2" /> : <TrendDown size={28} className="inline mr-2" />}
                        {fmtInr(nw)}
                    </div>
                    <div className="text-xs text-stone-600 mt-2">
                        {positive
                            ? `You'd keep ${fmtInr(nw)} after closing everything today.`
                            : `You'd need to bring in ${fmtInr(-nw)} extra to close everything today.`}
                    </div>
                </div>

                {/* Chart */}
                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        <span>How it&apos;s moving</span>
                        <div className="text-xs">
                            <Label className="mr-2">Months</Label>
                            <select className="border rounded-sm px-2 py-1 text-xs" value={months}
                                onChange={(e) => setMonths(Number(e.target.value))} data-testid="nw-months">
                                {[6, 12, 24, 36].map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="p-4" style={{ height: 320 }}>
                        {history && history.points?.length > 0 ? (
                            <ResponsiveContainer>
                                <LineChart data={history.points} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="label" fontSize={11} />
                                    <YAxis fontSize={11} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : `${(v / 1000).toFixed(0)}k`)} />
                                    <Tooltip formatter={(v) => fmtInr(v)} />
                                    <Legend />
                                    <Line type="monotone" dataKey="net_worth" name="Net worth" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="assets_realisable" name="Realisable assets" stroke="#0f766e" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                                    <Line type="monotone" dataKey="liabilities" name="Liabilities" stroke="#dc2626" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="text-sm text-stone-500 flex items-center justify-center h-full">No data.</div>
                        )}
                    </div>
                </div>

                {/* Breakdown */}
                {snap ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <BucketPanel title="Realisable assets" tone="teal"
                            total={snap.assets.realisable_total}
                            rows={[
                                { label: "Cash on hand", value: snap.assets.buckets.cash },
                                { label: "Bank balances", value: snap.assets.buckets.bank },
                                { label: "Receivables (customers)", value: snap.assets.buckets.receivables },
                                { label: "Stock (RM/WIP/FG at discount)", value: snap.assets.stock_realisable,
                                  detail: `Book ${fmtInr(snap.assets.stock_book)} → dilute` },
                                { label: "Fixed assets (net WDV)", value: snap.assets.buckets.fixed_assets },
                                { label: "GST / RCM ITC", value: snap.assets.buckets.gst_input },
                                { label: "Other assets (advances, deposits, etc.)", value: snap.assets.buckets.other },
                            ]} />
                        <BucketPanel title="Total liabilities" tone="rose"
                            total={snap.liabilities.total}
                            rows={[
                                { label: "Payables (vendors)", value: snap.liabilities.buckets.payables },
                                { label: "Loans outstanding", value: snap.liabilities.buckets.loans },
                                { label: "GST output payable", value: snap.liabilities.buckets.gst_output },
                                { label: "TDS payable", value: snap.liabilities.buckets.tds },
                                { label: "Customer advances", value: snap.liabilities.buckets.customer_advances },
                                { label: "Salary payable (staff)", value: snap.liabilities.buckets.salary_payable },
                                { label: "Contract payable (piece-rate)", value: snap.liabilities.buckets.contract_payable },
                                { label: "Other liabilities", value: snap.liabilities.buckets.other },
                            ]} />
                    </div>
                ) : null}

                {/* Stock breakdown */}
                {snap ? (
                    <div className="bg-white border rounded-md p-5" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg mb-3">Stock — dilution factors applied</div>
                        <div className="grid grid-cols-3 gap-4">
                            {(["rm", "wip", "fg"]).map((k) => {
                                const s = snap.assets.stock_breakdown[k];
                                return (
                                    <div key={k} className="border rounded-md p-3" style={{ borderColor: "var(--se-border)" }}>
                                        <div className="text-xs uppercase tracking-widest text-stone-500">{k === "rm" ? "Raw material" : k === "wip" ? "Work in progress" : "Finished goods"}</div>
                                        <div className="text-xs text-stone-500 mt-1">Book value</div>
                                        <div className="se-num">{fmtInr(s.book)}</div>
                                        <div className="text-xs text-stone-500 mt-2">Realisable @ {s.pct}%</div>
                                        <div className="se-num font-medium" style={{ color: "#0f766e" }}>{fmtInr(s.realisable)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                {/* Valuation settings */}
                <div className="bg-white border rounded-md p-5" style={{ borderColor: "var(--se-border)" }} data-testid="nw-settings">
                    <div className="font-serif text-lg mb-1 flex items-center gap-2">
                        <Sliders size={16} /> Dilution factors
                    </div>
                    <div className="text-xs text-stone-500 mb-4">Percent of book value you&apos;d actually realise on a fire sale. Adjust these and refresh to reflect a more or less aggressive close-shop scenario.</div>
                    <div className="grid grid-cols-3 gap-4">
                        {(["rm_pct", "wip_pct", "fg_pct"]).map((k) => (
                            <div key={k}>
                                <Label>{k === "rm_pct" ? "Raw material" : k === "wip_pct" ? "Work in progress" : "Finished goods"} (%)</Label>
                                <Input type="number" min={0} max={100} value={valuation[k]}
                                    onChange={(e) => setValuation((v) => ({ ...v, [k]: Number(e.target.value) }))}
                                    data-testid={`nw-${k}`} />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={saveSettings} disabled={!dirty || savingSettings}
                            style={{ background: "#f59e0b", color: "#fff" }} data-testid="nw-save-settings">
                            <FloppyDisk size={13} className="mr-1" /> {savingSettings ? "Saving..." : "Save & recompute"}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}

function BucketPanel({ title, total, rows, tone }) {
    const color = tone === "teal" ? "#0f766e" : "#991b1b";
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">{title}</div>
                <div className="se-num text-xl font-medium" style={{ color }}>{fmtInr(total)}</div>
            </div>
            <table className="w-full text-sm">
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: "var(--se-border)" }}>
                            <td className="px-5 py-2 text-stone-700">{r.label}
                                {r.detail ? <div className="text-[10px] text-stone-500">{r.detail}</div> : null}
                            </td>
                            <td className="px-5 py-2 text-right se-num">{fmtInr(r.value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
